/**
 * MobileItemChrome — per-item overlay shown while authoring a NON-desktop
 * breakpoint (tablet or mobile). Two affordances, scoped to the active tier:
 *   • a "<tier>" badge on items carrying a hand-placed position override (they
 *     no longer track the tier's default layout), and
 *   • a hide/show eye toggle: items hidden at this tier are dimmed on the canvas
 *     and dropped from the published render for that tier (a `display:none`
 *     @media rule). Overridden + hidden state live in the y-grid override layer.
 *
 * Rendered inside each grid item (renderWidget → DefaultGridItem), which is an
 * absolutely-positioned card, so this overlay's absolute children anchor to it.
 * react-strict-dom (html.* + css.create).
 */
import { useSyncExternalStore } from "react";
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import {
  spacing,
  microSpacing,
  radii,
} from "@duro-app/tokens/tokens/spacing.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { shadows } from "@duro-app/tokens/tokens/shadows.css";
import {
  readBreakpointFlags,
  setBreakpointHidden,
  clearBreakpointPosition,
  observeBreakpoints,
  type EditableBreakpoint,
} from "@website-builder/y-grid";
import { useEditorContextOptional } from "./EditorContext.js";
import { useSitemapInteraction } from "./SitemapInteractionContext.js";
import type { Breakpoint } from "./breakpoints.js";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";

const s = css.create({
  // Faint white veil over a hidden item — visual only, still selectable.
  dim: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.overlayLight,
    pointerEvents: "none",
    zIndex: 3,
  },
  chip: {
    position: "absolute",
    top: 4,
    right: 4,
    zIndex: 4,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  badge: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: microSpacing.px3,
    height: sizes.chip,
    paddingLeft: microSpacing.px6,
    paddingRight: spacing.sm,
    borderRadius: radii.full,
    backgroundColor: colors.infoBg,
    color: colors.accent,
    fontSize: "9.5px",
    fontWeight: typography.fontWeightBold,
    letterSpacing: "0.02em",
  },
  // "Reset to Desktop" — un-pins an overridden item so it follows the base again.
  resetBtn: {
    height: sizes.chip,
    paddingLeft: microSpacing.px6,
    paddingRight: spacing.sm,
    borderStyle: "none",
    borderRadius: radii.full,
    cursor: "pointer",
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    gap: microSpacing.px3,
    backgroundColor: { default: colors.infoBg, ":hover": colors.infoBorder },
    color: colors.accent,
    fontSize: "9.5px",
    fontWeight: typography.fontWeightBold,
    letterSpacing: "0.02em",
  },
  // Whisper-faint cue on items that still track the base (no override).
  inherited: {
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    gap: microSpacing.px2,
    height: sizes.handle,
    paddingLeft: microSpacing.px5,
    paddingRight: microSpacing.px6,
    borderRadius: radii.full,
    backgroundColor: colors.inverseFill,
    color: colors.textMuted,
    fontSize: "8.5px",
    fontWeight: typography.fontWeightSemibold,
    letterSpacing: "0.02em",
  },
  eyeBtn: {
    width: sizes.chip,
    height: sizes.chip,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderStyle: "none",
    borderRadius: radii.px6,
    cursor: "pointer",
    color: colors.textMuted,
    backgroundColor: colors.bg,
    boxShadow: shadows.sm,
  },
  eyeBtnOff: { color: colors.error },
});

const svg = (path: React.ReactNode) => (
  <svg
    viewBox="0 0 24 24"
    width={13}
    height={13}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block" }}
    aria-hidden
  >
    {path}
  </svg>
);
const EYE = svg(
  <>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);
const EYE_OFF = svg(
  <>
    <path d="M2 12s3.5-7 10-7c2 0 3.8.6 5.3 1.5M22 12s-3.5 7-10 7c-2 0-3.8-.6-5.3-1.5" />
    <path d="M4 4l16 16" />
  </>,
);
const PIN = svg(<path d="M12 2v6l3 3-3 3-3-3 3-3M12 14v8" />);
const RESET = svg(
  <>
    <path d="M3 8a7 7 0 1 1-1.5 4.3" />
    <path d="M3 3.5V8h4.5" />
  </>,
);
const DOWN = svg(<path d="M12 5v13M6 12l6 6 6-6" />);

/** Reactive "overridden,hidden" signature (a stable string so
 *  useSyncExternalStore doesn't re-render forever on a fresh object) for the
 *  active tier. Inert with no editor doc / on desktop (previews, isolated
 *  tests). */
function useTierSignature(
  ydoc: import("yjs").Doc | null,
  gridId: string,
  itemId: string,
  breakpoint: Breakpoint,
): string {
  return useSyncExternalStore(
    (cb) => (ydoc ? observeBreakpoints(ydoc, gridId, cb) : () => {}),
    () => {
      if (!ydoc || breakpoint === "desktop") return "00";
      const f = readBreakpointFlags(ydoc, gridId, itemId, breakpoint);
      return `${f.overridden ? 1 : 0}${f.hidden ? 1 : 0}`;
    },
    () => "00",
  );
}

export function MobileItemChrome({
  gridId,
  itemId,
}: {
  gridId: string;
  itemId: string;
}) {
  const { breakpoint } = useSitemapInteraction();
  const ydoc = useEditorContextOptional()?.ydoc ?? null;
  const sig = useTierSignature(ydoc, gridId, itemId, breakpoint);
  if (breakpoint === "desktop" || !ydoc) return null;
  const tier: EditableBreakpoint = breakpoint;

  const overridden = sig[0] === "1";
  const hidden = sig[1] === "1";

  return (
    <>
      {hidden && <html.div aria-hidden style={s.dim} />}
      <html.div style={s.chip}>
        {overridden && !hidden && (
          <>
            {/* Detached from the base: pinned for this device… */}
            <html.span style={s.badge}>
              {PIN} {tier}
            </html.span>
            {/* …and one click back to following Desktop. */}
            <html.button
              type="button"
              aria-label="Reset to Desktop layout"
              onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
              onClick={() =>
                clearBreakpointPosition(ydoc, gridId, itemId, tier)
              }
              style={s.resetBtn}
            >
              {RESET} reset
            </html.button>
          </>
        )}
        {!overridden && !hidden && (
          // Still tracking the base — the quiet counterpart to the loud pin.
          <html.span
            style={s.inherited}
            aria-label="Follows the Desktop layout"
          >
            {DOWN} base
          </html.span>
        )}
        <html.button
          type="button"
          aria-label={hidden ? `Show on ${tier}` : `Hide on ${tier}`}
          // Don't let the click start a grid drag / selection on the item.
          onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
          onClick={() =>
            setBreakpointHidden(ydoc, gridId, itemId, tier, !hidden)
          }
          style={[s.eyeBtn, hidden && s.eyeBtnOff]}
        >
          {hidden ? EYE_OFF : EYE}
        </html.button>
      </html.div>
    </>
  );
}
